import { useCallback } from "react"
import { Link } from "@tanstack/react-router"
import { IconChevronRightOutline18 as ChevronRight } from "nucleo-ui-outline-18"

import { Button } from "@productlane/ui"

import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { captureError } from "@/lib/capture-error"
import { useZeroWorkspace } from "@/lib/zero"

export const cardClass =
	"divide-y divide-gray-200 overflow-hidden rounded-[10px] bg-white shadow-card sm:-mx-3.5 dark:divide-gray-700/80 dark:bg-gray-850 dark:shadow-card-dark"

const sectionTitleClass = "text-base font-medium text-primary"

const formatInvoiceDate = (iso: string) =>
	new Date(iso).toLocaleDateString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
	})

const formatAmount = (amountInCents: number, currency: string) =>
	new Intl.NumberFormat(undefined, {
		style: "currency",
		currency: currency.toUpperCase(),
	}).format(amountInCents / 100)

export function useStripePortal() {
	const workspace = useZeroWorkspace()
	const managePlan = api.workspace.managePlan.useMutation()

	const open = useCallback(async () => {
		try {
			const url = await managePlan.mutateAsync()
			if (url) {
				window.open(url, "_blank")
			}
		} catch (error) {
			captureError(error, {
				domain: "billing",
				action: "open stripe portal",
				workspaceId: workspace?.id,
			})
			toast.error({ title: "Couldn't open your billing portal" })
		}
	}, [managePlan, workspace?.id])

	return { open, isLoading: managePlan.isLoading }
}

function DetailRow({ title, lines }: { title: string; lines: string[] }) {
	// One portal mutation per row, so only the clicked Edit shows a spinner.
	const portal = useStripePortal()

	return (
		<div className="flex items-center justify-between gap-4 px-4 py-5">
			<div className="flex min-w-0 flex-col gap-0.5">
				<span className="text-sm font-medium text-primary">{title}</span>
				{lines.map((line) => (
					<span key={line} className="truncate text-xs text-secondary">
						{line}
					</span>
				))}
			</div>
			<Button
				variant="ghost"
				size="sm"
				onClick={() => void portal.open()}
				isLoading={portal.isLoading}
				className="flex-none"
			>
				Edit
			</Button>
		</div>
	)
}

export function BillingDetailsSection() {
	const { data } = api.workspace.billingDetails.useQuery({ invoiceLimit: 3 })

	if (!data) return null

	const { billingInfo, paymentMethod } = data
	const brand = paymentMethod?.brand
		? paymentMethod.brand.replace(/^\w/, (letter) => letter.toUpperCase())
		: "Card"
	const cardLine = paymentMethod
		? `${brand} ending in ${paymentMethod.last4 ?? "----"}`
		: "Add a card to keep your subscription active"
	const billingLines = [billingInfo?.address, billingInfo?.email].filter(
		(line): line is string => !!line,
	)

	return (
		<div className="space-y-3 pt-6">
			<h2 className={sectionTitleClass}>Billing details</h2>

			<div className={cardClass}>
				<DetailRow
					title={billingInfo?.name ?? "Billing information"}
					lines={
						billingLines.length > 0
							? billingLines
							: ["Add your company name, address, and billing email"]
					}
				/>
				<DetailRow title="Payment method" lines={[cardLine]} />
				<DetailRow
					title="Tax identifier"
					lines={[
						billingInfo?.taxId ?? "Add a VAT or tax number to your invoices",
					]}
				/>
			</div>
		</div>
	)
}

export function InvoiceRow({
	invoice,
}: {
	invoice: {
		id: string
		createdIso: string
		total: number
		currency: string
		description: string
		url: string | null
	}
}) {
	return (
		<div className="group/invoice flex items-center justify-between gap-4 px-4 py-5">
			<div className="flex min-w-0 flex-col gap-0.5">
				<span className="text-sm font-medium text-primary">
					{formatInvoiceDate(invoice.createdIso)}
				</span>
				<span className="truncate text-xs text-secondary">
					{invoice.description}
				</span>
			</div>
			<div className="flex flex-none items-center gap-2">
				{invoice.url && (
					<a
						href={invoice.url}
						target="_blank"
						rel="noreferrer"
						className="opacity-0 transition-opacity focus-visible:opacity-100 group-hover/invoice:opacity-100 max-sm:opacity-100"
					>
						<Button variant="outline" size="sm">
							View
						</Button>
					</a>
				)}
				<span className="text-sm tabular-nums text-primary">
					{formatAmount(invoice.total, invoice.currency)}
				</span>
			</div>
		</div>
	)
}

export function RecentInvoicesSection() {
	const { data } = api.workspace.billingDetails.useQuery({ invoiceLimit: 3 })

	if (!data || data.invoices.length === 0) return null

	return (
		<div className="space-y-3 pt-6">
			<h2 className={sectionTitleClass}>Recent invoices</h2>

			<div className={cardClass}>
				{data.invoices.map((invoice) => (
					<InvoiceRow key={invoice.id} invoice={invoice} />
				))}
				{data.hasMoreInvoices && (
					<Link
						to="/settings/plans/invoices"
						className="flex items-center justify-between gap-4 px-4 py-5 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50"
					>
						<span className="text-sm font-medium text-primary">
							All invoices
						</span>
						<ChevronRight className="h-3.5 w-3.5 text-tertiary" />
					</Link>
				)}
			</div>
		</div>
	)
}
